// pages/Feedback.tsx
import React from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import { twMerge } from "tailwind-merge";
import Button from "../components/Button";
import DataTableView from "../components/DataTableView2";
import ExperienceRating from "../components/ExperienceRating";
import { TruncatedText } from "../components/truncated";
import useFeedback from '../hooks/logics/feedback/use-feedback-dashboard';
import { IGetAllFeedbackDataDocs } from "../utils/types";

export const experienceRatingMap: { [key: number]: string } = {
  1: "Terrible",
  2: "Bad",
  3: "Average",
  4: "Good",
  5: "Amazing",
};

const titleMap = { user: "User", driver: "Driver" };

const Feedback = () => {
  const {
    app,
    feedbacks,
    totalPages,
    loading,
    loadingRow,
    currentPage,
    setCurrentPage,
    unreadCount,
    handleAppChange,
    handleUpdateFeedbackStatus,
  } = useFeedback();

  const columns = [
    {
      header: "User name",
      className: "md:p-0 md:pl-10",
      accessor: (feedback: IGetAllFeedbackDataDocs) => (
        <div className="md:!pl-7">
          {feedback?.[app as keyof IGetAllFeedbackDataDocs]?.[0]?.fullName || "N/A"}
        </div>
      ),
    },
    {
      header: "Feedback",
      accessor: (feedback: IGetAllFeedbackDataDocs) =>
        feedback.feedback
          ? <TruncatedText text={feedback.feedback} limit={30} />
          : "N/A",
    },
    {
      header: "Rating",
      accessor: (feedback: IGetAllFeedbackDataDocs) => (
        <div className="flex flex-col w-[300px] gap-2">
          <div className="flex items-start gap-2 md:gap-4">
            <ExperienceRating emojiClassName="w-10 h-6" showLabel feedback={feedback} />
          </div>
        </div>
      ),
    },
    {
      header: "Actions",
      accessor: (feedback: IGetAllFeedbackDataDocs) => (
        <Button
          onClick={() => handleUpdateFeedbackStatus(feedback)}
          disabled={loadingRow === feedback?._id}
          className={twMerge("p-2 rounded-full")}
        >
          {feedback?.status === "read" ? <FaEyeSlash /> : <FaEye />}
        </Button>
      ),
    },
  ];

  return (
    <div className="w-full h-full">
      <div className="pb-4 flex flex-col justify-between font-inter bg-gray-100">
        <div className="flex flex-col md:flex-row  justify-between md:justify-end">
          <div className="flex items-center justify-center md:justify-end my-2 md:my-4">
            <div className="flex items-center gap-1 p-2 bg-white rounded-full">
              {(["user", "driver"] as const).map((type) => (
                <Button
                  key={type}
                  onClick={() => handleAppChange(type)}
                  className={`relative rounded-full px-6 py-2 transition-all ${app === type
                    ? "bg-primary text-white shadow-sm"
                    : "bg-transparent text-primary hover:bg-white"
                    }`}
                >
                  {titleMap[type]} App
                  {unreadCount[type] > 0 && (
                    <span className="absolute -top-2 -right-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1 text-xs text-white">
                      {unreadCount[type]}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <DataTableView
          data={feedbacks}
          columns={columns}
          isFetching={loading}
          emptyStateMessage="No feedbacks found"
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          rowClassName={(row: IGetAllFeedbackDataDocs) =>
            row?.status === "read" ? "" : "bg-[#DCFCE7]"
          }
        />
      </div>
    </div>
  );
};

export default Feedback;